import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  BarChart3, BookOpen, Check, ChevronDown, ChevronRight, Circle, Clock,
  FileDown, FileText, HelpCircle, LifeBuoy, ListChecks, LogOut, Menu,
  MoreHorizontal, PanelLeftClose, PencilLine, Plus, Settings, ShieldCheck, Upload, ClipboardCheck,
} from "lucide-react";
import NumaLogo from "@/components/NumaLogo";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useLLM } from "@/contexts/LLMContext";
import ThesisSelector from "@/components/ThesisSelector";
import LegalDialog from "@/components/LegalDialog";
import SupportDialog from "@/components/SupportDialog";
import { legal as legalApi } from "@/lib/api";
import { PROJECT_LABELS, unit, unitPlural } from "@/lib/projectTypes";

/**
 * `modes` says where an entry belongs.
 *
 * A manuscript brought in only for review has no use for a page about what to
 * write next, or for a progress chart towards a word count that is already
 * met. Leaving them clickable makes the product look like it has not
 * understood what it was asked to do.
 */
const NAV = [
  { key: "home", label: "Panoramica", icon: Menu },
  // Senza vincolo di modalità: si può volere una rilettura critica a metà
  // stesura, non solo su un manoscritto dichiarato finito. Il contrario -
  // «prima finisci, poi ti dico» - non è come si scrive.
  { key: "review", label: "Revisione", icon: ClipboardCheck },
  { key: "editor", label: "Editor", icon: PencilLine },
  { key: "sources", label: "Fonti", icon: BookOpen },
  { key: "start", label: "Da dove iniziare?", icon: HelpCircle, modes: ["write"] },
  { key: "dashboard", label: "Andamento", icon: BarChart3, modes: ["write"] },
  { key: "history", label: "Versioni", icon: Clock },
  { key: "export", label: "Esporta", icon: FileDown },
];

/**
 * Le pagine di una parte, come le direbbe chi ha il documento davanti.
 *
 * Il documento importato porta con sé, per ogni parte, la pagina in cui
 * comincia e quella in cui finisce - le pagine dell'originale, quelle che
 * l'autore ha stampato o consegnato. In revisione è l'unico numero che serve:
 * un rilievo si va a vedere a pagina 147, non al 62% di un capitolo. Un
 * documento scritto dentro NUMA non ha ancora pagine d'origine, e allora qui
 * non c'è niente da dire.
 */
function pagineDi(chapter) {
  const da = Number(chapter?.start_page) || 0;
  const a = Number(chapter?.end_page) || da;
  if (!da) return "";
  return a > da ? `pp. ${da}–${a}` : `p. ${da}`;
}

/** Quante pagine occupa: «4 pag.», il conto, non la posizione. */
function quantePagine(chapter) {
  const da = Number(chapter?.start_page) || 0;
  const a = Number(chapter?.end_page) || da;
  if (!da) return "";
  const quante = Math.max(1, a - da + 1);
  return `${quante} pag.`;
}

function StatusDot({ status }) {
  if (status === "completed") return <Check className="h-3.5 w-3.5 shrink-0 text-ok" aria-label="completata" />;
  if (status === "in_progress" || status === "draft")
    return <FileText className="h-3.5 w-3.5 shrink-0 text-brand" aria-label="in lavorazione" />;
  return <Circle className="h-3.5 w-3.5 shrink-0 text-ink-subtle" aria-label="vuota" />;
}

function SectionItem({ section, depth = 0 }) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = section.subsections?.length > 0;
  return (
    <div style={{ marginLeft: `${depth * 10 + 22}px` }}>
      <button
        type="button"
        onClick={() => hasChildren && setExpanded(!expanded)}
        className="flex w-full items-center gap-1 rounded px-1.5 py-1 text-left text-[12px] text-ink-muted hover:bg-surface-2"
        aria-expanded={hasChildren ? expanded : undefined}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />
        ) : (
          <span className="w-3" />
        )}
        <span className="truncate">{section.title}</span>
      </button>
      {expanded && hasChildren && section.subsections.map((sub, i) => (
        <SectionItem key={`${sub.title}-${i}`} section={sub} depth={depth + 1} />
      ))}
    </div>
  );
}

/**
 * Una parte del documento nell'indice.
 *
 * Capitoli e paragrafi stanno nello stesso elenco, distinti dal rientro: un
 * documento importato arriva spezzato su ogni titolo, e «1.1 Il metodo» va
 * letto sotto «Capitolo 1», non accanto. Il livello viene dal titolo che apre
 * la parte nel file d'origine (Titolo 1, Titolo 2, «2.3.1»), e oltre il quarto
 * non si rientra più: a quel punto la colonna sarebbe tutta margine.
 */
function ChapterItem({ chapter, isActive, onSelect, mode = "write" }) {
  const [expanded, setExpanded] = useState(false);
  const hasSections = chapter.sections?.length > 0;
  const livello = Math.min(Math.max(Number(chapter.level) || 1, 1), 4);
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(chapter.id)}
        aria-current={isActive ? "true" : undefined}
        data-level={livello}
        style={{ paddingLeft: `${10 + (livello - 1) * 14}px` }}
        className={`group w-full rounded-md px-2.5 py-1.5 text-left transition-colors ${
          isActive ? "bg-brand-soft" : "hover:bg-surface-2"
        }`}
      >
        <div className="flex items-center gap-2">
          {hasSections && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              className="-ml-1 shrink-0 text-ink-subtle"
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            </span>
          )}
          <StatusDot status={chapter.status} />
          <span
            className={`flex-1 truncate ${livello > 1 ? "text-[12px]" : "text-[13px]"} ${
              isActive ? "font-medium text-brand" : livello > 1 ? "text-ink-muted" : "text-ink"
            }`}
          >
            {chapter.title}
          </span>
          {/*
            In revisione la percentuale non vuol dire niente: il documento è
            finito, e «20% completato» su un capitolo di una tesi consegnata è
            un numero che non descrive nulla. Al suo posto le pagine. Mentre si
            scrive servono tutte e due: a che punto sono, e quanto è lungo.
          */}
          <span className="shrink-0 text-2xs tabular-nums text-ink-subtle">
            {mode === "review"
              ? pagineDi(chapter) || `${chapter.progress}%`
              : [`${chapter.progress}%`, quantePagine(chapter)].filter(Boolean).join(" · ")}
          </span>
        </div>
      </button>
      {expanded && hasSections && (
        <div className="mt-0.5 space-y-0.5">
          {chapter.sections.map((sec, i) => <SectionItem key={`${sec.title}-${i}`} section={sec} />)}
        </div>
      )}
    </li>
  );
}

/**
 * Tracks whether a scroll container has content above or below the fold.
 *
 * The outline fades its edges only when the list genuinely continues, so a list
 * that fits shows no decoration at all and a list that does not reads as
 * carrying on rather than as chopped off.
 */
function useScrollEdges(ref, deps) {
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setEdges({
      top: scrollTop > 4,
      bottom: scrollTop + clientHeight < scrollHeight - 4,
    });
  }, [ref]);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  return edges;
}

export default function Sidebar({
  thesis, theses = [], onThesisSelect, onThesisCreate, onThesisDelete,
  activeChapterId, onChapterSelect, view, onViewChange, onImport, onNewChapter,
  onCollapse,
}) {
  const { user, logout } = useAuth();
  const { status, openKeyDialog } = useLLM();
  const [legalDoc, setLegalDoc] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [owner, setOwner] = useState(null);

  const outlineRef = useRef(null);
  const chapterCount = thesis?.chapters?.length || 0;
  const edges = useScrollEdges(outlineRef, [chapterCount, thesis?.thesis_id]);

  useEffect(() => {
    legalApi.owner().then(setOwner).catch(() => setOwner(null));
  }, []);

  // Projects created before the two modes existed are projects being written.
  const mode = thesis?.project_mode === "review" ? "review" : "write";
  const units = unitPlural(thesis?.project_type);
  const unitLabel = unit(thesis?.project_type);

  return (
    <aside data-testid="sidebar" className="flex h-screen flex-col overflow-hidden border-r border-line bg-surface">
      {/* Wordmark and project picker share one compact block: every pixel here
          is a pixel the outline below does not get. */}
      <div className="px-3 pb-2.5 pt-4">
        <div className="mb-2.5 flex items-center gap-1">
          <button
            type="button"
            data-testid="nav-home-logo"
            onClick={() => onViewChange("home")}
            title="Torna alla panoramica"
            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-2"
          >
            <NumaLogo size={7} className="numa-mark" variant={0} />
            <span className="font-serif-display text-[16px] font-semibold leading-none text-ink">NUMA</span>
            <span className="sr-only">Vai alla panoramica</span>
          </button>
          <ThemeToggle />
        </div>
        <ThesisSelector
          thesis={thesis}
          theses={theses}
          onSelect={onThesisSelect}
          onCreate={onThesisCreate}
          onDelete={onThesisDelete}
        />
        {thesis?.project_type && (
          <p className="numa-meta mt-1 px-1">{PROJECT_LABELS[thesis.project_type] || "Documento"}</p>
        )}
      </div>

      {onCollapse && (
        <div className="px-3 pb-1">
          <button
            type="button"
            data-testid="collapse-sidebar"
            onClick={onCollapse}
            title="Nascondi il pannello (Ctrl+\\)"
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-line py-1 text-2xs text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
          >
            <PanelLeftClose className="h-3.5 w-3.5" /> Nascondi
          </button>
        </div>
      )}

      <nav className="px-3" aria-label="Sezioni principali">
        {NAV.map(({ key, label, icon: Icon, modes }) => {
          const offMode = modes && !modes.includes(mode);
          return (
            <button
              key={key}
              type="button"
              data-testid={`nav-${key}`}
              onClick={() => !offMode && onViewChange(key)}
              disabled={offMode}
              aria-current={view === key ? "page" : undefined}
              title={
                offMode
                  ? mode === "review"
                    ? "Non serve in revisione: questo progetto è un documento già scritto."
                    : "Disponibile sui progetti aperti in revisione."
                  : undefined
              }
              className="numa-nav-item numa-nav-compact disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-muted"
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          );
        })}
      </nav>

      {/* The outline takes every remaining pixel: nothing else sits below it. */}
      <div className="mt-4 flex min-h-0 flex-1 flex-col px-3">
        <div className="mb-1 flex items-center justify-between px-1.5">
          <h2 className="numa-eyebrow">
            {units}
            {chapterCount > 0 && (
              <span className="ml-1.5 font-normal tabular-nums text-ink-subtle">{chapterCount}</span>
            )}
          </h2>
          <div className="flex gap-0.5">
            <button
              type="button"
              data-testid="new-chapter-btn"
              onClick={onNewChapter}
              disabled={!thesis}
              title={
                thesis
                  ? `Aggiungi ${unitLabel.toLowerCase()}`
                  : "Serve un progetto aperto per aggiungere una sezione"
              }
              className="rounded p-1 text-ink-subtle transition-colors hover:bg-surface-2 hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="sr-only">Aggiungi {unitLabel.toLowerCase()}</span>
            </button>
            <button
              type="button"
              data-testid="import-thesis-btn"
              onClick={onImport}
              title="Importa un documento"
              className="rounded p-1 text-ink-subtle transition-colors hover:bg-surface-2 hover:text-ink"
            >
              <Upload className="h-3.5 w-3.5" />
              <span className="sr-only">Importa un documento</span>
            </button>
          </div>
        </div>

        <div className="relative min-h-0 flex-1">
          <div
            ref={outlineRef}
            className="numa-outline-scroll h-full overflow-y-auto pb-2"
            data-testid="chapter-outline"
          >
            {!thesis ? (
              <div className="numa-panel-quiet p-3 text-center">
                <p className="text-[12.5px] leading-relaxed text-ink-muted">Nessun progetto aperto.</p>
                <Button size="sm" variant="outline" onClick={onThesisCreate} className="mt-2.5 h-8 w-full">
                  <Plus className="h-3.5 w-3.5" /> Nuovo progetto
                </Button>
              </div>
            ) : (
              <ul className="space-y-0.5">
                {thesis.chapters?.map((ch) => (
                  <ChapterItem
                    key={ch.id}
                    chapter={ch}
                    mode={mode}
                    isActive={activeChapterId === ch.id}
                    onSelect={onChapterSelect}
                  />
                ))}
              </ul>
            )}
          </div>

          <div
            aria-hidden="true"
            className={`numa-scroll-fade numa-scroll-fade-top ${edges.top ? "is-visible" : ""}`}
          />
          <div
            aria-hidden="true"
            className={`numa-scroll-fade numa-scroll-fade-bottom ${edges.bottom ? "is-visible" : ""}`}
          />
        </div>
      </div>

      {/* One pinned row carries identity, model state and everything secondary,
          in place of the three stacked blocks that used to crowd the outline. */}
      <div className="flex items-center gap-1 border-t border-line px-2.5 py-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="account-menu"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-1 py-1 text-left transition-colors hover:bg-surface-2"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-2 text-[11px] font-medium text-ink-muted"
                aria-hidden="true"
              >
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-medium text-ink">
                  {user?.title ? `${user.title} ` : ""}{user?.name}
                </span>
                <span className="flex items-center gap-1.5">
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${status.ready ? "bg-ok" : "bg-warn"}`}
                    aria-hidden="true"
                  />
                  <span className="truncate text-2xs text-ink-subtle">
                    {status.ready ? status.model : "nessun modello collegato"}
                  </span>
                </span>
              </span>
              <MoreHorizontal className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" side="top" sideOffset={6} className="w-[250px]">
            <DropdownMenuLabel className="font-normal">
              <span className="block truncate text-[12.5px] font-medium text-ink">{user?.name}</span>
              <span className="block truncate text-2xs font-normal text-ink-subtle">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openKeyDialog()} className="cursor-pointer gap-2 text-[13px]">
              <ShieldCheck className="h-3.5 w-3.5" />
              {status.ready ? "Cambia modello" : "Collega un account o una chiave"}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onViewChange("settings")} className="cursor-pointer gap-2 text-[13px]">
              <Settings className="h-3.5 w-3.5" /> Impostazioni
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setSupportOpen(true)} className="cursor-pointer gap-2 text-[13px]">
              <LifeBuoy className="h-3.5 w-3.5" /> Assistenza
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setLegalDoc("privacy")} className="cursor-pointer gap-2 text-[13px]">
              Informativa privacy
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setLegalDoc("terms")} className="cursor-pointer gap-2 text-[13px]">
              Condizioni d&apos;uso
            </DropdownMenuItem>
            {owner?.configured && (
              <DropdownMenuLabel className="pt-1.5 text-2xs font-normal text-ink-subtle">
                &copy; {new Date().getFullYear()} {owner.name}
              </DropdownMenuLabel>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          data-testid="logout-btn"
          variant="ghost"
          size="icon"
          onClick={logout}
          title="Esci"
          className="h-8 w-8 shrink-0"
        >
          <LogOut className="h-4 w-4 text-ink-muted" />
          <span className="sr-only">Esci</span>
        </Button>
      </div>

      <LegalDialog
        open={Boolean(legalDoc)}
        onOpenChange={(o) => !o && setLegalDoc(null)}
        document={legalDoc || "privacy"}
      />
      <SupportDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        supportEmail={owner?.support_email}
      />
    </aside>
  );
}
